import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, History, Loader2, RotateCcw, Trash2, ChevronDown, AlertTriangle, Shield, ShieldCheck, Calendar, CalendarClock, Users, FileText, Pen, Clock, Plus, Link2, TrendingUp, Target } from 'lucide-react';
import { updateEntityPageTitle, clearEntityPageTitle } from '@/app/routes';
import {
  ChartContainer,
  ChartConfig,
  ChartTooltip,
  ChartTooltipContent
} from '@/components/ui/chart';
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import EditableField from '../../../components/ui/EditableField';
import { DatePicker } from '@/components/ui/date-picker';
import { generateFieldConfig, RiskFieldConfig } from './fieldsConfig';
import { TreatRisksHeatmap } from './TreatRisksHeatmap';
import { cleanDateAndTimeObject, renderDate, toLocalDateString } from '../../../../shared/renderDate';
import './riskdetail.css';
import { useTheme } from '@/contexts/ThemeContext';
import { colorValues } from '@/contexts/ThemeContext';
import { getAuthUID, isAuthLoaded, onAuthReady } from '@/lib/authState';
import { useTenancy } from '@/contexts/TenancyContext';
import { Risk } from '@/types/risks';
import { getRisk, putRisk, createRisk, getMenus, deleteRisk } from '@/services/risksService';
import { getAsset, updateAsset } from '@/services/assetsService';
import type { AssetRegister } from '@/js/external-js/variableModules/namespaces/AssetRegister.namespace';
import { toast } from 'sonner';
import { getAllRisks } from '../../../services/risksService';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faHistory } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { routePathByName } from '@/app/routes';
import { getCompanyInfo } from '@/services/companyService';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { RiskLinkedDocuments } from '@/components/risk/RiskLinkedDocuments';
import RiskHistoryDialog from './RiskHistoryDialog';

interface RiskPageProps {
  isReadOnly?: boolean;
  onBackNavigation?: () => void;
}

type RiskEntity = Risk & { riskHistory?: any[] };

const RiskPage: React.FC<RiskPageProps> = ({ isReadOnly = false, onBackNavigation }) => {
  const [uid, setUid] = useState<string | null>(isAuthLoaded() ? getAuthUID() : null);
  const { activeTenancy, users, userIdToName } = useTenancy();
  const tenancyId = activeTenancy?.id || window.companyID;
  const [isCreateMode, setIsCreateMode] = useState(false);

  const { id: riskID } = useParams();
  const [riskItem, setRiskItem] = useState<Risk>();
  const [riskFieldsConfig, setRiskFieldsConfig] = useState<RiskFieldConfig[]>([]);
  const [riskHistory, setRiskHistory] = useState<Record<string, any>[]>([]);
  const [nextRiskId, setNextRiskId] = useState<string>("");
  const [changes, setChanges] = useState<Record<string, any>>();
  const [saving, setSaving] = useState(false);
  const activeFieldRef = useRef<string | null>(null);
  const heatmapRef = useRef<{ refreshHeatmap: () => void }>(null);


  const [isEditing, setIsEditing] = useState(false);
  const [activeEditField, setActiveEditField] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true); // Set to true to show overlay
  const [notFound, setNotFound] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isRiskHistoryDialogOpen, setIsRiskHistoryDialogOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('details');
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [viewingHistoricalVersion, setViewingHistoricalVersion] = useState<boolean>(false);
  const [historicalRisk, setHistoricalRisk] = useState<RiskEntity | null>(null);
  const [isBannerSticky, setIsBannerSticky] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Inline title editing state
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [pendingDisplayName, setPendingDisplayName] = useState('');
  const [comparisonRiskIndex, setComparisonRiskIndex] = useState(0);
  const [isRotating, setIsRotating] = useState(false);
  const [otherRisks, setOtherRisks] = useState<Risk[]>([]);
  const [loadingRisks, setLoadingRisks] = useState(false);

  // Auto-rotate comparison risk every 5 seconds
  useEffect(() => {
    if (!isRotating) return;

    const interval = setInterval(() => {
      setComparisonRiskIndex((prev) => (prev + 1) % otherRisks.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isRotating, otherRisks.length]);

  // Stop rotation on user interaction or navigate to next risk
  const handleRiskComparisonClick = () => {
    if (isRotating) {
      setIsRotating(false);
    } else {
      // Navigate to next risk when not rotating
      setComparisonRiskIndex((prev) => (prev + 1) % otherRisks.length);
    }
  };

  // Fetch other risks for comparison
  const fetchOtherRisks = useCallback(async () => {
    if (!tenancyId || !riskItem) return;

    setLoadingRisks(true);
    try {
      const allRisks = await getAllRisks(tenancyId);
      // Filter out current risk and get up to 10 other risks
      const filteredRisks = allRisks
        .filter(risk => risk.riskID !== riskItem.riskID && risk.id !== riskItem.id)
        .slice(0, 10);
      setOtherRisks(filteredRisks);
    } catch (error) {
      console.error('Failed to fetch other risks:', error);
      toast.error('Failed to load comparison risks');
    } finally {
      setLoadingRisks(false);
    }
  }, [tenancyId, riskItem]);

  // Fetch other risks when current risk loads
  useEffect(() => {
    if (riskItem && tenancyId) {
      fetchOtherRisks();
    }
  }, [riskItem, tenancyId, fetchOtherRisks]);

  // Get current comparison risk
  const currentComparisonRisk = otherRisks[comparisonRiskIndex];


  const syncRiskAssetLinks = useCallback(async (riskId: string, previousAssetIds: string[], nextAssetIds: string[]) => {
    const companyId = tenancyId;
    if (!companyId || !riskId) return true;

    const normalize = (ids: string[]) => ids.filter(Boolean).map(id => id.toString());
    const prev = normalize(previousAssetIds);
    const next = normalize(nextAssetIds);

    const toAdd = next.filter(id => !prev.includes(id));
    const toRemove = prev.filter(id => !next.includes(id));

    if (!toAdd.length && !toRemove.length) return true;

    const tasks: Promise<void>[] = [];

    const updateAssetRisks = (assetId: string, action: 'add' | 'remove') => tasks.push((async () => {
      const assetInstance = await getAsset(assetId);
      const assetEntity = assetInstance?.entity as AssetRegister.AssetEntity | undefined;
      if (!assetEntity) return;

      const existingRisksSource = Array.isArray((assetEntity as any).assetRisks)
        ? (assetEntity as any).assetRisks
        : Array.isArray((assetEntity as any).risks)
          ? (assetEntity as any).risks
          : [];
      const existingRisks = existingRisksSource.filter(Boolean).map((id: any) => id.toString());

      let updatedRisks = existingRisks;
      if (action === 'add') {
        if (existingRisks.includes(riskId.toString())) return;
        updatedRisks = [...existingRisks, riskId.toString()];
      } else {
        if (!existingRisks.includes(riskId.toString())) return;
        updatedRisks = existingRisks.filter(id => id !== riskId.toString());
      }

      await updateAsset(assetId.toString(), {
        risks: updatedRisks,
      } as Partial<AssetRegister.AssetEntity>);
    })());

    toAdd.forEach(assetId => updateAssetRisks(assetId, 'add'));
    toRemove.forEach(assetId => updateAssetRisks(assetId, 'remove'));

    const results = await Promise.allSettled(tasks);
    const failed = results.filter(result => result.status === 'rejected');
    failed.forEach(result => console.error('[RiskDetail] Failed syncing risk-asset relationship', result));

    if (failed.length > 0) {
      throw new Error('One or more asset updates failed');
    }

    return true;
  }, [tenancyId]);


  // Set up scroll listener and measure banner height
  useEffect(() => {
    if (!bannerRef.current) return;

    // Measure banner height
    setBannerHeight(bannerRef.current.offsetHeight);

    const handleScroll = () => {
      if (!bannerRef.current) return;

      const scrollPosition = window.scrollY;
      const bannerPosition = bannerRef.current.offsetTop;
      const bannerHeight = bannerRef.current.offsetHeight;

      // Make banner sticky when scrolled past its original position
      setIsBannerSticky(scrollPosition > bannerPosition);
    };

    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    // Initial check
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [viewingHistoricalVersion]);

  // Handle version selection - shows the state of the risk before the selected change was made
  const handleVersionSelect = (index: number) => {
    console.log('handleVersionSelect called with index:', index);
    console.log('Current riskHistory:', riskHistory);

    setSelectedVersion(index);
    if (index !== null) {
      // Start with the current risk state (deep copy)
      const versionedRisk = JSON.parse(JSON.stringify(riskItem));

      // Sort history by date (most recent first)
      const sortedHistory = [...riskHistory].sort((a, b) =>
        (cleanDateAndTimeObject(b.dateChanged)?.getTime() || 0) -
        (cleanDateAndTimeObject(a.dateChanged)?.getTime() || 0)
      );

      console.log('Sorted history:', sortedHistory);

      // Get the selected change
      const selectedChange = sortedHistory[index];
      console.log('Selected change:', selectedChange);

      // If this is the first change, we need to handle it specially
      if (index === sortedHistory.length - 1) {
        // For the oldest change, we only need to apply the old values from this change
        const changes = selectedChange.riskNew || selectedChange.changes || {};
        Object.entries(changes).forEach(([key, changeValue]) => {
          // Handle both old and new format
          if (selectedChange.changes) {
            const change = changeValue as { elementName: string; oldValue: any; newValue: any };
            console.log(`  Reverting ${key} to old value:`, change.oldValue);
            // Special handling for riskRating to ensure it's properly set
            if (key === 'riskRating' && change.oldValue !== undefined) {
              versionedRisk[key] = change.oldValue;
            } else {
              versionedRisk[key] = change.oldValue;
            }
          } else {
            // Old format - just take the value as is
            versionedRisk[key] = changeValue;
          }
        });

        console.log('Final versionedRisk state:', versionedRisk);
        setHistoricalRisk(versionedRisk);
        setViewingHistoricalVersion(true);
        return;
      }

      // For non-first changes, get all changes up to but not including the selected one
      const changesToApply: Record<string, any> = {};

      // Process changes in chronological order (oldest first) up to but not including the selected change
      sortedHistory.slice(index + 1).reverse().forEach(historyItem => {
        const changes = historyItem.riskNew || historyItem.changes || {};

        Object.entries(changes).forEach(([key, changeValue]) => {
          if (historyItem.changes) {
            // New format with oldValue/newValue
            const change = changeValue as { elementName: string; oldValue: any; newValue: any };
            // Only apply if we haven't seen this field before (we're going from old to new)
            if (changesToApply[key] === undefined) {
              changesToApply[key] = change.oldValue;
            }
          } else {
            // Old format - just take the value as is
            changesToApply[key] = changeValue;
          }
        });
      });

      // Now apply all the changes to reconstruct the historical state
      console.log('Changes to apply to reconstruct historical state:', changesToApply);

      // First, apply all the changes from history up to the selected point
      Object.entries(changesToApply).forEach(([key, value]) => {
        console.log(`  Setting ${key} to:`, value);
        // Handle nested objects by merging them
        if (value && typeof value === 'object' && !Array.isArray(value) && versionedRisk[key] && typeof versionedRisk[key] === 'object') {
          versionedRisk[key] = { ...versionedRisk[key], ...value };
        } else {
          versionedRisk[key] = value;
        }
      });

      // Then, apply the old values from the selected change to ensure we show the state BEFORE this change
      const selectedChanges = selectedChange.riskNew || selectedChange.changes || {};
      Object.entries(selectedChanges).forEach(([key, changeValue]) => {
        if (selectedChange.changes) {
          const change = changeValue as { elementName: string; oldValue: any; newValue: any };
          if (change.oldValue !== undefined) {
            console.log(`  Reverting ${key} to old value from selected change:`, change.oldValue);
            versionedRisk[key] = change.oldValue;
          }
        }
      });

      console.log('Final versionedRisk state:', versionedRisk);
      setHistoricalRisk(versionedRisk);
      setViewingHistoricalVersion(true);
      console.log('Historical view enabled for version', index);
    } else {
      console.log('Exiting historical view');
      setViewingHistoricalVersion(false);
      setHistoricalRisk(null);
    }
  };

  const exitHistoricalView = () => {
    setViewingHistoricalVersion(false);
    setHistoricalRisk(null);
    setSelectedVersion(null);
  };

  const controlIds = React.useMemo(() => {
    const sel = Array.isArray((riskItem as any)?.selectedControls) ? (riskItem as any).selectedControls : [];
    const rel = Array.isArray((riskItem as any)?.relatedControls) ? (riskItem as any).relatedControls : [];
    return Array.from(new Set([...sel, ...rel].filter(Boolean).map((x: any) => String(x))));
  }, [JSON.stringify((riskItem as any)?.selectedControls || []), JSON.stringify((riskItem as any)?.relatedControls || [])]);

  // Default entity link for this Risk
  const defaultEntityLink = React.useMemo(() => {
    const ref = String((riskItem?.riskID ?? riskItem?.id) ?? '');
    const title = String(riskItem?.risk || riskItem?.description || 'Current Risk');
    if (!ref) return undefined as any;
    return {
      id: `internal:risk:${ref}`,
      ref,
      title,
      domain: 'Risk',
      relation: 'mitigates',
    };
  }, [riskItem?.riskID, riskItem?.id, riskItem?.risk, riskItem?.description]);

  // Mappings card now handles its own discovery and loading; no local effect needed

  // Determine which risk to display - current or historical version
  const displayRisk = viewingHistoricalVersion && historicalRisk ? historicalRisk : riskItem;

  const handleOnSave = async (fieldKey: string, value) => {
    console.log('[Risk Detail] onSave', fieldKey, value);

    // Record the active field being edited
    activeFieldRef.current = fieldKey;

    // For create mode, just update state without API call
    if (isCreateMode) {
      setRiskItem(prevState => {
        const updates: any = { [fieldKey]: value };
        
        // Sync treatRequired and treatmentRequired for backward compatibility
        if (fieldKey === 'treatRequired') {
          updates.treatmentRequired = value;
        } else if (fieldKey === 'treatmentRequired') {
          updates.treatRequired = value;
        }
        
        return {
          ...prevState,
          ...updates
        } as Risk;
      });

      // Also track changes in create mode
      setChanges(prev => {
        const changes: any = { [fieldKey]: value };
        
        // Sync treatRequired and treatmentRequired for backward compatibility
        if (fieldKey === 'treatRequired') {
          changes.treatmentRequired = value;
        } else if (fieldKey === 'treatmentRequired') {
          changes.treatRequired = value;
        }
        
        return {
          ...(prev || {}),
          ...changes
        };
      });

      toast.success(`Updated ${fieldKey}`);
      return;
    }

    // For existing risks, save to backend
    toast.loading(`Saving ${fieldKey}...`);

    try {
      // Create a minimal Risk object with only the required ID and the field being updated
      const updates: any = { [fieldKey]: value };
      
      // Sync treatRequired and treatmentRequired for backward compatibility
      if (fieldKey === 'treatRequired') {
        updates.treatmentRequired = value;
      } else if (fieldKey === 'treatmentRequired') {
        updates.treatRequired = value;
      }
      
      const riskUpdate = {
        ...riskItem,  // Include all existing properties to maintain type integrity
        ...updates, // Include the field update and sync
      };

      // Remove riskHistory because the backend handles it
      const riskUpdateCopy = { ...riskUpdate };
      delete riskUpdateCopy.riskHistory;

      await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);

      // Refresh the heatmap after saving
      heatmapRef.current?.refreshHeatmap();

      if (fieldKey === 'assets') {
        const previousAssets = Array.isArray(riskItem?.assets)
          ? riskItem.assets.map(assetId => assetId?.toString?.() ?? '')
          : [];
        const nextAssets = Array.isArray(value)
          ? value.map(assetId => assetId?.toString?.() ?? '')
          : typeof value === 'string'
            ? value.split(',').map(assetId => assetId.trim()).filter(Boolean)
            : [];

        try {
          // DED-1261: Use risk.id (componentId || riskID) for consistency with RisksCombobox
          const riskIdForSync = (riskItem as any)?.componentId?.toString?.() ?? riskItem?.riskID?.toString?.() ?? riskID ?? '';
          await syncRiskAssetLinks(riskIdForSync, previousAssets, nextAssets);
          toast.success('Risk-asset relationships synced successfully');
        } catch (syncError) {
          console.error('[Risk Detail] Failed to sync risk-asset relationships', syncError);
          toast.error('Failed to sync risk-asset relationships');
        }
      }

      setRiskItem(prevState => {
        // Create a properly typed Risk object with the updated field
        const updatedRisk = {
          ...prevState,
          [fieldKey]: value,
        };
        return updatedRisk as Risk;
      });

      // Track changes for edit mode too
      setChanges(prev => ({
        ...(prev || {}),
        [fieldKey]: value
      }));

      toast.dismiss();
      toast.success(`Saved ${fieldKey}`);

      // Clear active field
      activeFieldRef.current = null;
    } catch (error) {
      toast.dismiss();
      toast.error(`Failed to save ${fieldKey}`);
      console.error(error);

      // Clear active field
      activeFieldRef.current = null;
    }
  };

  const handleTitleRenameSave = async () => {
    if (!riskItem || isCreateMode) {
      setIsRenamingTitle(false);
      return;
    }

    const trimmed = pendingDisplayName.trim();

    // If no change, just exit edit mode
    if (trimmed === (riskItem as any).displayName) {
      setIsRenamingTitle(false);
      return;
    }

    // If empty, reset to original value
    if (!trimmed) {
      setIsRenamingTitle(false);
      return;
    }

    try {
      toast.loading('Updating risk title...');

      // Update the risk with the new displayName
      await handleOnSave('displayName', trimmed);
      setIsRenamingTitle(false);
    } catch (error) {
      console.error('Failed to update risk title:', error);
      toast.error('Failed to update risk title');
    }
  };

  const addMcmControlToRisk = useCallback(async (controlId: string) => {
    if (!riskItem || !tenancyId || isCreateMode) return;

    const currentRelated = Array.isArray((riskItem as any)?.selectedControls)
      ? (riskItem as any).selectedControls
      : [];

    // Check if already exists
    if (currentRelated.includes(controlId)) {
      console.log(`[RiskDetail] Control ${controlId} already in selectedControls`);
      return;
    }

    // Add to array
    const updatedRelated = [...currentRelated, controlId];

    console.log(`[RiskDetail] Adding ${controlId} to selectedControls`);

    try {
      // Direct Firestore update without triggering full state refresh
      const riskUpdate = {
        ...riskItem,
        selectedControls: updatedRelated,
      };
      const riskUpdateCopy = { ...riskUpdate } as any;
      delete riskUpdateCopy.riskHistory;

      await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);

      // Update local state only
      setRiskItem(prevState => ({
        ...prevState,
        selectedControls: updatedRelated,
      } as Risk));

      console.log(`[RiskDetail] Added ${controlId} to selectedControls`);
    } catch (e) {
      console.warn('[RiskDetail] Failed to add control to selectedControls', e);
    }
  }, [riskID, tenancyId, isCreateMode]);

  // Clear entity title when component unmounts
  useEffect(() => {
    return () => {
      clearEntityPageTitle();
    };
  }, []);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteRisk(tenancyId, (riskItem.riskID || riskItem.id));
      navigate(routePathByName('risk-register'));
      toast.success('Risk deleted successfully');
    } catch (error) {
      toast.error('Failed to delete risk');
      console.error(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    if (!tenancyId) {
      console.log('no tenant id');
      return;
    }

    const loadRisk = async () => {
      try {
        // Check if we're creating a new risk
        if (riskID === 'new') {
          setIsCreateMode(true);
          setIsEditing(true);

          // Get menus for field configuration
          const menus = await getMenus();

          // Set up default risk values
          const today = new Date();
          const nextReviewDate = new Date(today);
          nextReviewDate.setFullYear(today.getFullYear() + 1);

          // Fetch current risks to generate next risk ID
          try {
            // We need to fetch existing risks to calculate the next risk ID
            const currentRisks = await getAllRisks(tenancyId);

            // Calculate next risk ID (similar to risk-register implementation)
            const existingRiskIds = new Set(currentRisks.map(risk => risk.riskID));
            let maxId = 0;

            currentRisks.forEach(risk => {
              const idNum = parseInt(risk.riskID || "0", 10);
              if (!isNaN(idNum) && idNum > maxId) {
                maxId = idNum;
              }
            });

            // Increment to get the next ID
            const nextId = (maxId + 1).toString();
            setNextRiskId(nextId);

            console.log(`Generated next risk ID: ${nextId}`);

            const defaultRisk = {
              id: nextId,
              riskID: nextId,
              riskIDstatus: "active",
              risk: 'Low',
              businessUnit: menus?.businessUnit?.[0] || "",
              cause: "",
              componentId: `RSK-${nextId}`,
              controlEffect: menus?.controlEffect?.[0] || "No credible control - 1",
              controls: "",
              dateAdded: new Date().toISOString(),
              impact: "",
              inherentRisk: "Low",
              lastReviewDate: new Date().toISOString(),
              nextReviewDate: nextReviewDate.toISOString(),
              residualRisk: "Low",
              response: "",
              riskOwner: "",
              selectedControls: [],
              targetRisk: "Low",
              treatRequired: "No",
              treatmentRequired: "No",
              treatmentPlan: "",
              version: "1.0",
              status: 'Active',
              type: 'Other',
              riskCategory: '',
              criticality: 'Medium',
              dataClassification: 'Business Confidential',
              subprocessor: false,
              publishInPrivacyPolicy: false,
              annualSecurityReviewCompleted: false,
              vendorManagementNeeded: false,
              dataTypesShared: [],
              dataStorageLocations: [],
              portable: false,
              serialNumber: '',
              physicalLabel: false,
              socCriticality: 'N/A',
              backupsRequired: false,
              riskRating: 'Medium',
              category: '',
              owner: '',
              description: '',
              history: [{
                changedBy: uid || "Unknown",
                dateChanged: new Date(),
                riskNew: {
                  status: "Created via form"
                }
              }],
              riskHistory: [],
              assets: [],
              menus: menus || {}
            } as Risk;

            setRiskItem(defaultRisk);
            const fields = generateFieldConfig(menus, users || [], true); // Pass isCreateMode=true
            setRiskFieldsConfig(fields);
            setLoading(false);
          } catch (error) {
            console.error("Error calculating next risk ID:", error);

            // Create default risk without ID if there was an error
            const defaultRisk = {
              id: "",
              riskID: "0",
              riskIDstatus: "active",
              risk: 'Low',
              businessUnit: menus?.businessUnit?.[0] || "",
              cause: "",
              componentId: "",
              controlEffect: menus?.controlEffect?.[0] || "No credible control - 1",
              controls: "",
              dateAdded: new Date().toISOString(),
              impact: "",
              inherentRisk: "Low",
              lastReviewDate: new Date().toISOString(),
              nextReviewDate: nextReviewDate.toISOString(),
              residualRisk: "Low",
              response: "",
              riskOwner: "",
              selectedControls: [],
              targetRisk: "Low",
              treatRequired: "No",
              treatmentRequired: "No",
              treatmentPlan: "",
              version: "1.0",
              status: 'Active',
              type: 'Other',
              riskCategory: '',
              criticality: 'Medium',
              dataClassification: 'Business Confidential',
              subprocessor: false,
              publishInPrivacyPolicy: false,
              annualSecurityReviewCompleted: false,
              vendorManagementNeeded: false,
              dataTypesShared: [],
              dataStorageLocations: [],
              portable: false,
              serialNumber: '',
              physicalLabel: false,
              socCriticality: 'N/A',
              backupsRequired: false,
              riskRating: 'Medium',
              category: '',
              owner: '',
              description: '',
              history: [{
                changedBy: uid || "Unknown",
                dateChanged: new Date().toISOString(),
                riskNew: {
                  status: "Created via form"
                }
              }],
              riskHistory: [],
              menus: menus || {}
            } as Risk;

            setRiskItem(defaultRisk);
            const fields = generateFieldConfig(menus, users || [], true);
            setRiskFieldsConfig(fields);
            setLoading(false);
          }
          return;
        }

        // Load existing risk
        // const risk = await getRisk(tenancyId, riskID);
        // console.log(`Getting risk: ${riskID}:`, risk.menus, users, uid);
        // setIsCreateMode(false);
        // setRiskItem(risk.entry);

        // const fields = generateFieldConfig(risk.menus, users, false); // Pass isCreateMode=false
        // setRiskFieldsConfig(fields);
        // setLoading(false);
        // Load existing risk
        let risk: Risk | null = null;
        try {
          console.log(`[Risk Detail] Getting risk: ${riskID}:`, tenancyId, riskID)
          risk = await getRisk(tenancyId, riskID);
          setIsCreateMode(false);
        } catch (error) {
          console.error('Error loading risk:', error, error.code, error.status, error.message);
          toast.error('Failed to load risk or not found');
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (!risk) {
          console.error('Error loading risk: risk is null/undefined');
          toast.error('Failed to load risk or not found');
          setNotFound(true);
          setLoading(false);
          return;
        }

        // Handle both formats: direct risk object or { entry: risk, menus: ... }
        const riskData = (risk as any).entry ?? risk;
        const riskMenus = (risk as any).menus;

        console.log(`[Risk Detail] Got risk: ${riskID}:`, riskMenus, users, uid);
        console.log('[Risk Detail] Got risk:', risk);
        setIsCreateMode(false);
        // Sync treatRequired and treatmentRequired for backward compatibility
        if (riskData.treatRequired && !riskData.treatmentRequired) {
          riskData.treatmentRequired = riskData.treatRequired;
        } else if (riskData.treatmentRequired && !riskData.treatRequired) {
          riskData.treatRequired = riskData.treatmentRequired;
        }

        setRiskItem(({
          ...riskData,
          assets: Array.isArray(riskData.assets)
            ? riskData.assets
            : Array.isArray(riskData.relatedAssets)
              ? riskData.relatedAssets
              : [],
        } as unknown) as Risk);

        // Update page title with risk information
        const riskTitle = riskData.displayName || riskData.risk || riskData.description || 'Risk';
        const riskId = riskData.id || riskData.riskID || riskID;
        updateEntityPageTitle(riskTitle, riskId.toString(), 'risk');
        if (riskData.riskHistory && riskData.riskHistory.length > 0) {
          const sortedHistory = riskData.riskHistory.sort((a: any, b: any) => (cleanDateAndTimeObject(b.dateChanged)?.getTime() || 0) - (cleanDateAndTimeObject(a.dateChanged)?.getTime() || 0));
          setRiskHistory(sortedHistory);
        } else {
          setRiskHistory([]);
        }

        // Get fresh menus to ensure we have the latest risk categories
        const freshMenus = await getMenus();
        console.log('[Risk Detail] Got fresh menus:', freshMenus);

        // Get business units from company settings (customerOrg.teams.businessUnitNames)
        const companyInfo = await getCompanyInfo(tenancyId);
        const businessUnitNames = (companyInfo?.customerOrg?.teams?.businessUnitNames || {}) as Record<string, { name: string }>;
        const companyBusinessUnits = Object.values(businessUnitNames).map(bu => bu.name);
        console.log('[Risk Detail] Got businessUnits from company settings:', companyBusinessUnits);

        // Combine with fresh menus businessUnit and de-duplicate
        const existingBusinessUnits = freshMenus?.businessUnit || [];
        const deduplicatedBusinessUnits = [...new Set([...existingBusinessUnits, ...companyBusinessUnits])];
        console.log('[Risk Detail] Deduplicated businessUnits:', deduplicatedBusinessUnits);

        freshMenus.businessUnit = deduplicatedBusinessUnits;
        console.log('[Risk Detail] Updated fresh menus:', freshMenus);

        const fields = generateFieldConfig(freshMenus, users, false); // Pass isCreateMode=false with fresh menus
        setRiskFieldsConfig(fields);
        setLoading(false);
      } catch (error) {
        console.error('Error loading risk:', error);
        toast.error('Failed to load risk');
        setLoading(false);
      }
    };

    loadRisk();
  }, [tenancyId, riskID]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-overlay">
          <div className="spinner" />
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  // Show friendly error page if risk not found
  if (notFound) {
    return (
      <div className="page-container">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '6rem',
            marginBottom: '1rem'
          }}>
            🦆
          </div>
          <h1 style={{
            fontSize: '2rem',
            marginBottom: '1rem',
            color: theme.isDark ? '#f3f4f6' : '#18181b'
          }}>
            Quack! Risk Not Found
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '2rem',
            maxWidth: '600px',
            color: theme.isDark ? '#d1d5db' : '#4b5563'
          }}>
            Sorry, we couldn't find the risk you're looking for. It may have been deleted or never existed.
          </p>
          {!isReadOnly && (
            <button
              className="primary-btn"
              style={{
                background: colorValues[theme.primaryColor]?.[theme.isDark ? 'dark' : 'light']?.primary ?? colorValues.default.light.primary,
                color: theme.isDark ? '#fff' : '#18181b',
                border: `1px solid ${colorValues[theme.primaryColor]?.[theme.isDark ? 'dark' : 'light']?.primary ?? colorValues.default.light.primary}`,
                padding: '0.75rem 2rem',
                fontSize: '1.1rem',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              onClick={() => onBackNavigation ? onBackNavigation() : navigate('/risk-register')}
            >
              <i className="fa-solid fa-chevron-left"></i> {onBackNavigation ? 'Back to Auditor Risk Register' : 'Back to Risk Register'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Historical Version Banner */}
      {viewingHistoricalVersion && historicalRisk && selectedVersion !== null && (
        <div
          ref={bannerRef}
          className={`w-full bg-yellow-50 dark:bg-yellow-900/90 border-b border-yellow-200 dark:border-yellow-800 py-2 transition-all duration-200 ${isBannerSticky ? 'fixed top-0 left-0 right-0 z-40 shadow-md' : ''}`}
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faHistory} className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  Viewing historical version from {renderDate(riskHistory[selectedVersion]?.dateChanged, { dateStyle: 'long' })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleVersionSelect(null)}
                className="text-sm font-medium text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 rounded px-2 py-1"
              >
                Return to current version
              </button>
            </div>
          </div>
        </div>
      )}

      {isBannerSticky && viewingHistoricalVersion && <div className="h-10 w-full" />}

      {/* Hero Section */}
      <div className="relative w-full mx-auto px-8 py-12 bg-linear-to-br from-primary/15 via-background to-muted/20">
        <div className="pointer-events-none absolute inset-0 opacity-30 blur-3xl bg-[radial-gradient(circle_at_top,var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent"></div>
        <div className="relative w-full max-w-[1400px] mx-auto px-8 py-12 bg-linear-to-br from-muted/10 via-muted/5 to-transparent">
          {!isReadOnly && (
            <button
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 w-fit"
              onClick={() => onBackNavigation ? onBackNavigation() : navigate('/risk-register')}
            >
              <ArrowLeft className="h-4 w-4" />
              {onBackNavigation ? 'Back to Auditor Risk Register' : 'Back to Risk Register'}
            </button>
          )}

          <div className="flex items-center justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-1.5 text-sm font-medium text-foreground">
                  <Target className="h-4 w-4" />
                  {displayRisk?.componentId || `RSK-${displayRisk?.riskID || 'NEW'}`}
                </span>
                {displayRisk?.riskRating && (
                  <span className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium ${(displayRisk.riskRating as string) === 'Extreme'
                      ? 'border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400'
                      : (displayRisk.riskRating as string) === 'High'
                        ? 'border-orange-500/50 bg-orange-500/10 text-orange-600 dark:text-orange-400'
                        : (displayRisk.riskRating as string) === 'Medium'
                          ? 'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400'
                    }`}>
                    <Shield className="h-4 w-4" />
                    {displayRisk.riskRating} Risk
                  </span>
                )}
                {displayRisk?.status && (
                  <span className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${displayRisk.status === 'Active'
                      ? 'border-primary/50 bg-primary/10 text-primary'
                      : 'border-muted-foreground/50 bg-muted text-muted-foreground'
                    }`}>
                    {displayRisk.status}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 group mb-2" data-force-isms-override>
                {isRenamingTitle && !isReadOnly && !isCreateMode ? (
                  <input
                    className="text-3xl font-bold text-foreground bg-transparent border-b-2 border-primary/50 focus:outline-none focus:border-primary"
                    autoFocus
                    value={pendingDisplayName}
                    onChange={(e) => setPendingDisplayName(e.target.value)}
                    onBlur={handleTitleRenameSave}
                    data-force-isms-override
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleTitleRenameSave();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setIsRenamingTitle(false);
                      }
                    }}
                  />
                ) : (
                  <h1
                    className="text-3xl font-bold text-foreground whitespace-normal wrap-break-word"
                    data-force-isms-override
                  >
                    {displayRisk?.displayName || displayRisk?.risk || 'Untitled Risk'}
                  </h1>
                )}
                {!isReadOnly && !isCreateMode && !isRenamingTitle && (
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                    onClick={() => {
                      const effectiveName = displayRisk?.displayName || displayRisk?.risk || '';
                      setPendingDisplayName(effectiveName);
                      setIsRenamingTitle(true);
                    }}
                    aria-label="Rename risk"
                  >
                    <Pen className="h-5 w-5" />
                  </button>
                )}
              </div>
              <p className="text-muted-foreground">
                {displayRisk?.riskCategory || 'Uncategorized'}
                {displayRisk?.businessUnit && ` • ${displayRisk.businessUnit}`}
              </p>
              {/* Review Dates */}
              <div className="flex flex-wrap items-center gap-3 mt-3 opacity-70">
                {/* Last Review Date */}
                {(!isCreateMode && !isReadOnly) ? (
                  <div className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    <span className="text-xs text-muted-foreground/80">Last:</span>
                    <DatePicker
                      date={displayRisk?.lastReviewDate ? new Date(displayRisk.lastReviewDate) : undefined}
                      setDate={async (date) => {
                        try {
                          toast.loading('Updating last review date...');
                          await handleOnSave('lastReviewDate', date);
                        } catch (error) {
                          console.error('Failed to update last review date:', error);
                          toast.error('Failed to update last review date');
                        }
                      }}
                      placeholder="Not set"
                      enableNaturalInput={true}
                      showPreview={false}
                      hideCalendarIcon={true}
                      disableInputPadding={true}
                      allowCalendarOnClick={true}
                      className="text-xs bg-transparent border-0 outline-none h-auto min-w-[80px] text-muted-foreground/80 [&_input]:bg-transparent [&_input]:border-0 [&_input]:outline-none [&_input]:p-0 [&_input]:m-0 [&_input]:text-xs [&_input]:text-muted-foreground/80 [&_input]:rounded-none [&_input]:focus:outline-none [&_input]:focus:shadow-none [&_input:active]:outline-none [&_input:active]:shadow-none [&_input]:selection:bg-transparent [&_input]:caret-transparent"
                    />
                  </div>
                ) : (
                  displayRisk?.lastReviewDate && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/80">
                      <Calendar className="h-3 w-3" />
                      Last: {new Date(displayRisk.lastReviewDate).toLocaleDateString()}
                    </span>
                  )
                )}

                {/* Next Review Date */}
                {(!isCreateMode && !isReadOnly) ? (
                  <div className="inline-flex items-center gap-1.5">
                    <CalendarClock className="h-3 w-3" />
                    <span className="text-xs text-muted-foreground/80">Next:</span>
                    <DatePicker
                      date={displayRisk?.nextReviewDate ? new Date(displayRisk.nextReviewDate) : undefined}
                      setDate={async (date) => {
                        try {
                          toast.loading('Updating next review date...');
                          await handleOnSave('nextReviewDate', date);
                        } catch (error) {
                          console.error('Failed to update next review date:', error);
                          toast.error('Failed to update next review date');
                        }
                      }}
                      placeholder="Not set"
                      enableNaturalInput={true}
                      showPreview={false}
                      hideCalendarIcon={true}
                      disableInputPadding={true}
                      allowCalendarOnClick={true}
                      className="text-xs bg-transparent border-0 outline-none h-auto min-w-[80px] text-muted-foreground/80 [&_input]:bg-transparent [&_input]:border-0 [&_input]:outline-none [&_input]:p-0 [&_input]:m-0 [&_input]:text-xs [&_input]:text-muted-foreground/80 [&_input]:rounded-none [&_input]:focus:outline-none [&_input]:focus:shadow-none [&_input:active]:outline-none [&_input:active]:shadow-none [&_input]:selection:bg-transparent [&_input]:caret-transparent"
                    />
                  </div>
                ) : (
                  displayRisk?.nextReviewDate && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/80">
                      <CalendarClock className="h-3 w-3" />
                      Next: {new Date(displayRisk.nextReviewDate).toLocaleDateString()}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isCreateMode ? (
                <>
                  {!isReadOnly && (
                    <Button
                      variant="outline"
                      className="border-destructive/30 bg-destructive/15 text-destructive hover:bg-destructive/25"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setIsRiskHistoryDialogOpen(true)}>
                    <History className="mr-2 h-4 w-4" />
                    History
                  </Button>
                </>
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      setSaving(true);
                      toast.loading('Creating new risk...');
                      activeFieldRef.current = null;

                      if (!riskItem) {
                        toast.error('No risk data to save');
                        setSaving(false);
                        return;
                      }

                      const { menus, ...riskDataToSave } = riskItem;
                      const riskToSave = {
                        ...riskDataToSave,
                        menus: menus || {}
                      };
                      const result = await createRisk(tenancyId, nextRiskId, riskToSave as Risk);

                      try {
                        const createdRiskId = (result?.riskID ?? result?.id ?? nextRiskId)?.toString?.() ?? nextRiskId;
                        const selectedAssets = Array.isArray(riskItem.assets)
                          ? riskItem.assets.map(assetId => assetId?.toString?.() ?? '')
                          : [];
                        await syncRiskAssetLinks(createdRiskId, [], selectedAssets);
                        toast.success('Risk-asset relationships synced successfully');
                      } catch (syncError) {
                        console.error('[Risk Detail] Failed to sync risk-asset relationships on create', syncError);
                        toast.error('Failed to sync risk-asset relationships');
                      }

                      toast.dismiss();
                      toast.success('Risk created successfully');

                      setRiskItem(({
                        ...result,
                        assets: Array.isArray((result as any)?.assets)
                          ? (result as any).assets
                          : Array.isArray(riskItem.assets)
                            ? riskItem.assets
                            : [],
                        menus: (result as any)?.menus ?? riskItem.menus ?? {},
                      } as unknown) as Risk);
                      setIsCreateMode(false);
                      setIsEditing(false);
                      setSaving(false);
                      setChanges(undefined);
                      navigate(`/riskdetail/${result.id}`);
                    } catch (error) {
                      toast.dismiss();
                      toast.error('Failed to create risk');
                      console.error('Error creating risk:', error);
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                  Create Risk
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-10 border-t border-white/10 bg-card/95 px-8 py-8 bg-linear-to-br from-background via-background to-muted/5">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Column - Risk Summary */}
            <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-24 lg:h-fit">
              {/* Risk Description Card */}
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-6 py-4 border-b border-border" style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                  <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Risk Overview
                  </h3>
                </div>
                <div className="p-6 bg-muted/10 space-y-6">
                  {/* Quick Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="rounded-lg bg-muted/30 p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Shield className="h-4 w-4" />
                        <span className="text-xs font-medium">Inherent Risk</span>
                      </div>
                      <p className="text-lg font-semibold">{displayRisk?.inherentRisk || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-muted/30 p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Shield className="h-4 w-4" />
                        <span className="text-xs font-medium">Residual Risk</span>
                      </div>
                      <p className="text-lg font-semibold">{displayRisk?.residualRisk || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-muted/30 p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Target className="h-4 w-4" />
                        <span className="text-xs font-medium">Target Risk</span>
                      </div>
                      <p className="text-lg font-semibold">{displayRisk?.targetRisk || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-muted/30 p-4">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs font-medium">Next Review</span>
                      </div>
                      <p className="text-lg font-semibold">{displayRisk?.nextReviewDate ? renderDate(displayRisk.nextReviewDate) : 'N/A'}</p>
                    </div>
                  </div>

                  {/* Event, Cause, Impact, and Treatment Plan */}
                  <div className="space-y-4">
                    {/* Event */}
                    <div className="rounded-lg bg-muted/20 p-4">
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">Event</h4>
                      <p className="text-sm text-foreground">{displayRisk?.risk || 'No event description provided'}</p>
                    </div>

                    {/* Cause & Impact */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {displayRisk?.cause && (
                        <div className="rounded-lg bg-muted/20 p-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Cause</h4>
                          <p className="text-sm text-foreground">{displayRisk.cause}</p>
                        </div>
                      )}
                      {displayRisk?.impact && (
                        <div className="rounded-lg bg-muted/20 p-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Impact</h4>
                          <p className="text-sm text-foreground">{displayRisk.impact}</p>
                        </div>
                      )}
                    </div>

                    {/* Control Effectiveness & Response */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {displayRisk?.controlEffect && (
                        <div className="rounded-lg bg-muted/20 p-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Control Effectiveness</h4>
                          <p className="text-sm text-foreground">{displayRisk.controlEffect}</p>
                        </div>
                      )}
                      {displayRisk?.response && (
                        <div className="rounded-lg bg-muted/20 p-4">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Response</h4>
                          <p className="text-sm text-foreground">{displayRisk.response}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Analytics Card */}
              {!isCreateMode && (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <div className="px-6 py-4 border-b border-border" style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                    <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Risk Analytics
                    </h3>
                  </div>
                  <div className="p-6 bg-muted/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Risk Level Gauge */}
                      <div className="rounded-lg bg-card border p-4">
                        <h4 className="text-sm font-medium text-muted-foreground mb-4">Risk Level</h4>
                        <ChartContainer
                          config={{
                            risk: {
                              label: "Risk Level",
                              color: displayRisk?.inherentRisk === 'Extreme' ? "hsl(0 84% 60%)"
                                : displayRisk?.inherentRisk === 'High' ? "hsl(25 95% 53%)"
                                  : displayRisk?.inherentRisk === 'Medium' ? "hsl(45 93% 47%)"
                                    : "hsl(142 71% 45%)"
                            }
                          } satisfies ChartConfig}
                          className="h-[160px] w-full"
                        >
                          <RadialBarChart
                            innerRadius="60%"
                            outerRadius="100%"
                            data={[{
                              name: 'Risk',
                              value: displayRisk?.inherentRisk === 'Extreme' ? 100
                                : displayRisk?.inherentRisk === 'High' ? 75
                                  : displayRisk?.inherentRisk === 'Medium' ? 50
                                    : displayRisk?.inherentRisk === 'Low' ? 25 : 0,
                              fill: displayRisk?.inherentRisk === 'Extreme' ? "hsl(0 84% 60%)"
                                : displayRisk?.inherentRisk === 'High' ? "hsl(25 95% 53%)"
                                  : displayRisk?.inherentRisk === 'Medium' ? "hsl(45 93% 47%)"
                                    : "hsl(142 71% 45%)"
                            }]}
                            startAngle={180}
                            endAngle={0}
                          >
                            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                            <RadialBar dataKey="value" cornerRadius={10} />
                          </RadialBarChart>
                        </ChartContainer>
                        <p className={`text-center text-lg font-semibold mt-2 ${displayRisk?.inherentRisk === 'Extreme' ? 'text-red-600 dark:text-red-400'
                            : displayRisk?.inherentRisk === 'High' ? 'text-orange-600 dark:text-orange-400'
                              : displayRisk?.inherentRisk === 'Medium' ? 'text-amber-600 dark:text-amber-400'
                                : 'text-green-600 dark:text-green-400'
                          }`}>{displayRisk?.inherentRisk || 'N/A'}</p>
                        <p className="text-center text-xs text-muted-foreground">Inherent Risk Level</p>
                      </div>

                      {/* Risk Metrics */}
                      <div className="rounded-lg bg-card border p-4">
                        <h4 className="text-sm font-medium text-muted-foreground mb-4">Risk Metrics</h4>
                        <ChartContainer
                          config={{
                            inherent: { label: "Inherent", color: "hsl(0 84% 60%)" },
                            residual: { label: "Residual", color: "hsl(221 83% 53%)" },
                            target: { label: "Target", color: "hsl(142 71% 45%)" }
                          } satisfies ChartConfig}
                          className="h-[160px] w-full"
                        >
                          <BarChart
                            layout="vertical"
                            data={[
                              { name: 'Inherent', value: displayRisk?.inherentRisk === 'Extreme' ? 4 : displayRisk?.inherentRisk === 'High' ? 3 : displayRisk?.inherentRisk === 'Medium' ? 2 : 1, fill: 'hsl(0 84% 60%)' },
                              { name: 'Residual', value: displayRisk?.residualRisk === 'Extreme' ? 4 : displayRisk?.residualRisk === 'High' ? 3 : displayRisk?.residualRisk === 'Medium' ? 2 : 1, fill: 'hsl(221 83% 53%)' },
                              { name: 'Target', value: displayRisk?.targetRisk === 'Extreme' ? 4 : displayRisk?.targetRisk === 'High' ? 3 : displayRisk?.targetRisk === 'Medium' ? 2 : 1, fill: 'hsl(142 71% 45%)' }
                            ]}
                            margin={{ left: 80, right: 20 }}
                          >
                            <XAxis type="number" domain={[0, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 12 }} tickFormatter={(value) => ['Low', 'Medium', 'High', 'Extreme'][value - 1] || ''} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
                            <ChartTooltip content={<ChartTooltipContent className='bg-card' />} />
                            <Bar dataKey="value" radius={4} />
                          </BarChart>
                        </ChartContainer>
                        <div className="flex justify-center gap-4 mt-2 text-xs">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Inherent</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Residual</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Target</span>
                        </div>
                      </div>
                    </div>

                    {/* Risk Heatmap for Treat Risks */}
                    <TreatRisksHeatmap ref={heatmapRef} />

                    {/* Risk vs Risk Comparison - Hidden for now */}
                    {/* <div className="mt-6 rounded-lg bg-card border p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-muted-foreground">Risk Comparison</h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setComparisonRiskIndex((prev) => (prev - 1 + otherRisks.length) % otherRisks.length)}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous risk"
                            disabled={loadingRisks || otherRisks.length === 0}
                          >
                            <i className="fa-solid fa-chevron-left text-xs"></i>
                          </button>
                          <button
                            onClick={() => setIsRotating(!isRotating)}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isRotating ? "Stop rotation" : "Start rotation"}
                            disabled={loadingRisks || otherRisks.length === 0}
                          >
                            <i className={`fa-solid fa-${isRotating ? 'pause' : 'play'} text-xs`}></i>
                          </button>
                          <button
                            onClick={() => setComparisonRiskIndex((prev) => (prev + 1) % otherRisks.length)}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next risk"
                            disabled={loadingRisks || otherRisks.length === 0}
                          >
                            <i className="fa-solid fa-chevron-right text-xs"></i>
                          </button>
                        </div>
                      </div>
                      <ChartContainer
                        config={{
                          currentRisk: { label: displayRisk?.displayName || "Current Risk", color: "hsl(221 83% 53%)" },
                          otherRisk: { label: currentComparisonRisk?.displayName || currentComparisonRisk?.risk || "Other Risk", color: "hsl(0 74% 54%)" }
                        } satisfies ChartConfig}
                        className="h-[180px] w-full cursor-pointer"
                        onClick={handleRiskComparisonClick}
                      >
                        <BarChart
                          data={otherRisks.length > 0 ? [
                            { 
                              metric: 'Inherent', 
                              current: displayRisk?.inherentRisk === 'Extreme' ? 4 : displayRisk?.inherentRisk === 'High' ? 3 : displayRisk?.inherentRisk === 'Medium' ? 2 : 1,
                              comparison: currentComparisonRisk?.inherentRisk === 'Extreme' ? 4 : currentComparisonRisk?.inherentRisk === 'High' ? 3 : currentComparisonRisk?.inherentRisk === 'Medium' ? 2 : 1
                            },
                            { 
                              metric: 'Residual', 
                              current: displayRisk?.residualRisk === 'Extreme' ? 4 : displayRisk?.residualRisk === 'High' ? 3 : displayRisk?.residualRisk === 'Medium' ? 2 : 1,
                              comparison: currentComparisonRisk?.residualRisk === 'Extreme' ? 4 : currentComparisonRisk?.residualRisk === 'High' ? 3 : currentComparisonRisk?.residualRisk === 'Medium' ? 2 : 1
                            }
                          ] : [
                            { 
                              metric: 'Inherent', 
                              current: displayRisk?.inherentRisk === 'Extreme' ? 4 : displayRisk?.inherentRisk === 'High' ? 3 : displayRisk?.inherentRisk === 'Medium' ? 2 : 1,
                            },
                            { 
                              metric: 'Residual', 
                              current: displayRisk?.residualRisk === 'Extreme' ? 4 : displayRisk?.residualRisk === 'High' ? 3 : displayRisk?.residualRisk === 'Medium' ? 2 : 1,
                            }
                          ]}
                          margin={{ left: 80, right: 30, top: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="metric" tick={{ fontSize: 12 }} />
                          <YAxis domain={[0, 4]} ticks={[0, 1, 2, 3, 4]} tick={{ fontSize: 12 }} tickFormatter={(value) => ['Low', 'Medium', 'High', 'Extreme'][value - 1] || ''} />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="current" fill="hsl(221 83% 53%)" radius={[4, 4, 0, 0]} />
                          {otherRisks.length > 0 && (
                            <Bar dataKey="comparison" fill="hsl(0 74% 54%)" radius={[4, 4, 0, 0]} />
                          )}
                        </BarChart>
                      </ChartContainer>
                      <div className="flex justify-center gap-6 mt-3 text-xs">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                          <span>Current Risk</span>
                        </span>
                        {otherRisks.length > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            <span>Other Risk</span>
                          </span>
                        )}
                      </div>
                      <div className="mt-3 space-y-1">
                        {loadingRisks ? (
                          <p className="text-xs text-center text-muted-foreground">Loading comparison risks...</p>
                        ) : otherRisks.length === 0 ? (
                          <p className="text-xs text-center text-muted-foreground">No other risks found</p>
                        ) : (
                          <>
                            <p className="text-xs font-medium text-center text-foreground">
                              Comparing this Risk to {currentComparisonRisk?.displayName || currentComparisonRisk?.risk || `Risk ${currentComparisonRisk?.riskID}`}
                            </p>
                            <p className="text-xs text-center text-muted-foreground">
                              {currentComparisonRisk?.category || 'Uncategorized'} • {comparisonRiskIndex + 1} of {otherRisks.length} • Click chart to {isRotating ? 'stop' : 'manually navigate'}
                            </p>
                          </>
                        )}
                      </div>
                    </div> */}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Metadata Sidebar */}
            <div className="lg:col-span-1 space-y-4">

              {/* Render each section as a collapsible */}
              {Array.from(new Set(riskFieldsConfig.map(f => f.section))).map((section, sectionIndex) => (
                <Collapsible key={section} defaultOpen={sectionIndex === 0}>
                  <div className="rounded-xl border bg-card overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div
                        className="group flex cursor-pointer items-center justify-between px-4 py-3 transition-all hover:bg-accent"
                        style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}
                      >
                        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">{section}</h3>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t border-border p-4 space-y-3 bg-muted/20">
                        {riskFieldsConfig.filter(f => f.section === section).map(field => {
                          // Hide Treatment Plan if Treatment Required doesn't indicate it should be shown
                          if (field.key === 'treatmentPlan') {
                            const treatmentRequired = displayRisk?.treatmentRequired;
                            const shouldShowTreatmentPlan = treatmentRequired &&
                              typeof treatmentRequired === 'string' &&
                              (treatmentRequired.toLowerCase().includes('yes') ||
                                treatmentRequired.toLowerCase().includes('required') ||
                                treatmentRequired.toLowerCase().includes('treat'));
                            if (!shouldShowTreatmentPlan) {
                              return null;
                            }
                          }

                          // Get field value with Risk Title defaulting logic
                          let fieldValue = displayRisk?.[field.key];
                          if (field.key === 'displayName' && !fieldValue) {
                            // Risk Title defaults to Event value when unset
                            fieldValue = displayRisk?.risk || '';
                          }

                          return (
                            <EditableField
                              key={field.key}
                              id={field.key}
                              label={field.label}
                              value={fieldValue}
                              type={field.type}
                              options={field.options}
                              className="bg-muted"
                              disabled={field.readOnly || viewingHistoricalVersion || isReadOnly}
                              editing={!isReadOnly && !viewingHistoricalVersion && (isCreateMode || activeEditField === field.key)}
                              setEditing={(editing) => {
                                if (isReadOnly || viewingHistoricalVersion) return;
                                if (editing) setActiveEditField(field.key);
                                else setActiveEditField(null);
                              }}
                              onSave={value => {
                                if (viewingHistoricalVersion) return;

                                if (isCreateMode) {
                                  setRiskItem(prev => ({ ...prev, [field.key]: value }));
                                  setChanges(prev => ({ ...(prev || {}), [field.key]: value }));
                                } else {
                                  handleOnSave(field.key, value);
                                  setActiveEditField(null);
                                }
                              }}
                              isCreateMode={isCreateMode}
                              hideActionButton={isCreateMode || viewingHistoricalVersion}
                              fullWidth={field.fullWidth}
                            />
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}

              {/* Change History Section */}
              {/* {!isCreateMode && (
                <Collapsible defaultOpen={false}>
                  <div className="rounded-xl border bg-card overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="group flex cursor-pointer items-center justify-between px-4 py-3 transition-all hover:bg-accent" style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Change History</h3>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t border-border p-4 bg-muted/20">
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {riskHistory.length > 0 ? (
                            riskHistory.filter(item => {
                              const changes = Object.entries(item.riskNew || item.changes || {});
                              return changes.some(([_, changeValue]) => {
                                const change = changeValue as { oldValue: any; newValue: any };
                                const oldVal = item.changes ? change.oldValue : (changeValue as any);
                                const newVal = item.changes ? change.newValue : (changeValue as any);
                                return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                              });
                            }).map((item, index) => {
                              const formattedDate = renderDate(item.dateChanged, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                              const changedByName = item.changedBy ? (userIdToName[item.changedBy] || 'Unknown user') : 'Unknown user';
                              const changes = Object.entries(item.riskNew || item.changes || {}).filter(([_, changeValue]) => {
                                const change = changeValue as { oldValue: any; newValue: any };
                                const oldVal = item.changes ? change.oldValue : (changeValue as any);
                                const newVal = item.changes ? change.newValue : (changeValue as any);
                                return JSON.stringify(oldVal) !== JSON.stringify(newVal);
                              });

                              return (
                                <div key={index} className="group relative overflow-hidden rounded-lg border border-border/50 bg-linear-to-r from-background via-background to-muted/20 hover:border-border hover:shadow-sm transition-all duration-200">
                                  <div className="absolute inset-0 bg-linear-to-r from-transparent via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                  <div className="relative p-3">
                                    <div className="flex items-start gap-3">
                                      <div className="shrink-0 mt-1">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary">
                                          <Edit className="h-4 w-4" />
                                        </div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                          <p className="text-sm font-semibold text-foreground">Risk Updated</p>
                                          <span className="text-xs text-muted-foreground/70 whitespace-nowrap">{formattedDate}</span>
                                        </div>
                                        {changes.length > 0 ? (
                                          <div className="space-y-1.5 mb-2">
                                            {changes.slice(0, 3).map(([key, changeValue], idx) => {
                                              const change = changeValue as { elementName?: string; oldValue: any; newValue: any };
                                              const fieldName = change.elementName || key;
                                              return (
                                                <div key={idx} className="flex items-start gap-2 group/item">
                                                  <div className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-1.5 shrink-0"></div>
                                                  <p className="text-xs text-muted-foreground/80 leading-relaxed flex-1">{fieldName} updated</p>
                                                </div>
                                              );
                                            })}
                                            {changes.length > 3 && (
                                              <p className="text-xs text-primary/80">+{changes.length - 3} more changes</p>
                                            )}
                                          </div>
                                        ) : (
                                          <p className="text-xs text-muted-foreground/60 italic mb-2">Risk metadata updated</p>
                                        )}
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
                                          <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30"></div>
                                          <span>By {changedByName}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center py-8">
                              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                                <Clock className="h-8 w-8 text-muted-foreground/40" />
                              </div>
                              <p className="text-sm text-muted-foreground/60">No history available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )} */}

              {/* Linked Documents Section */}
              {!isCreateMode && (
                <Collapsible defaultOpen={false}>
                  <div className="rounded-xl border bg-card overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <div className="group flex cursor-pointer items-center justify-between px-4 py-3 transition-all hover:bg-accent" style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                        <div className="flex items-center gap-2">
                          {/* <Link2 className="h-4 w-4 text-muted-foreground" /> */}
                          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Linked Documents</h3>
                          <Badge variant="secondary" className="ml-2">
                            {Array.isArray((riskItem as any)?.linkedDocuments) ? (riskItem as any).linkedDocuments.length : 0}
                          </Badge>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t border-border p-4 bg-muted/20">
                        {riskItem && (
                          <RiskLinkedDocuments
                            riskId={riskItem.riskID?.toString() || riskItem.id?.toString() || ''}
                            riskOwner={riskItem.riskOwner}
                            riskNextReviewDate={riskItem.nextReviewDate}
                            riskControlIds={controlIds}
                            linkedDocuments={Array.isArray((riskItem as any).linkedDocuments) ? (riskItem as any).linkedDocuments : []}
                            onDocumentsChange={async (docIds) => {
                              if (!tenancyId || !riskItem) return;
                              try {
                                const riskUpdate = {
                                  ...riskItem,
                                  linkedDocuments: docIds,
                                };
                                const riskUpdateCopy = { ...riskUpdate } as any;
                                delete riskUpdateCopy.riskHistory;
                                await putRisk(tenancyId, riskItem.riskID, riskUpdateCopy as Risk);
                                setRiskItem(prev => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    linkedDocuments: docIds,
                                  } as RiskEntity;
                                });
                                toast.success('Linked documents updated');
                              } catch (error) {
                                console.error('Failed to update linked documents:', error);
                                toast.error('Failed to update linked documents');
                              }
                            }}
                            isReadOnly={isReadOnly || viewingHistoricalVersion}
                          />
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* MCM Mappings Card */}
              <MCMMappingsCard
                tenancyId={tenancyId}
                isReadOnly={isReadOnly}
                controlIds={controlIds}
                defaultEntityLink={defaultEntityLink}
                onEnsureControlLinked={addMcmControlToRisk}
              />
            </div>
          </div>
        </div>

        {/* Additional Sections */}
        <div className="w-full max-w-[1400px] mx-auto px-8 pb-8 space-y-6">
          {/* Back Button */}
          {!isReadOnly && (
            <button
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => navigate('/risk-register')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Risk Register
            </button>
          )}
        </div>
      </div>
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Delete Risk</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this risk? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The risk will not be recoverable.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Risk
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Risk History Dialog */}
      <RiskHistoryDialog
        editHistory={(() => {
          // Convert riskHistory array to the format expected by RiskHistoryDialog
          const editHistoryMap: Record<string, any> = {};
          riskHistory.forEach((item, index) => {
            editHistoryMap[index.toString()] = {
              changeID: index,
              changedBy: item.changedBy || 'Unknown',
              dateChanged: item.dateChanged,
              fieldsChanged: item.changes || item.riskNew || {}
            };
          });
          return editHistoryMap;
        })()}
        reviewHistory={(() => {
          // Convert reviewHistory array to the format expected by RiskHistoryDialog
          const reviewHistoryMap: Record<string, any> = {};
          const riskReviewHistory = (riskItem as any)?.reviewHistory || [];
          if (Array.isArray(riskReviewHistory)) {
            riskReviewHistory.forEach((item: any, index: number) => {
              reviewHistoryMap[index.toString()] = item;
            });
          }
          return reviewHistoryMap;
        })()}
        isOpen={isRiskHistoryDialogOpen}
        onClose={() => setIsRiskHistoryDialogOpen(false)}
      />
    </div>
  );
};

export default RiskPage;
